/**
 * Swift bridge generator - Functional implementation with dependency injection
 * Generates Objective-C bridge files for React Native integration
 */

import type { ModuleConstant, ModuleEvent, ModuleMethod } from "../../parser";
import { isAnyObjectType } from "../../utils/type-helpers";
import type { SwiftCodeEmitter } from "./swift-code-emitter";
import type { SwiftTypeMapper } from "./swift-type-mapper";

export interface SwiftBridgeGeneratorDeps {
  typeMapper: SwiftTypeMapper;
  codeEmitter: SwiftCodeEmitter;
}

export interface SwiftBridgeGenerator {
  /**
   * Generates BrickCodegen.h umbrella header
   */
  generateBrickCodegenHeader(): string;

  /**
   * Generates BrickModule.h header
   */
  generateBrickModuleHeader(
    events: Array<ModuleEvent & { moduleName: string }>
  ): string;

  /**
   * Generates BrickModule.mm Objective-C bridge implementation
   */
  generateObjectiveCBridge(
    allMethods: Array<ModuleMethod & { moduleName: string }>,
    allConstants: Array<ModuleConstant & { moduleName: string }>,
    allEvents: Array<ModuleEvent & { moduleName: string }>
  ): string;

  /**
   * Generates main BrickModule.swift file content
   */
  generateBrickModuleSwift(
    allEvents: Array<ModuleEvent & { moduleName: string }>
  ): string;

  /**
   * Generates supported events array (Objective-C format)
   */
  generateSupportedEventsArray(
    events: Array<ModuleEvent & { moduleName: string }>
  ): string;

  /**
   * Generates Swift supported events array
   */
  generateSwiftSupportedEventsArray(
    events: Array<ModuleEvent & { moduleName: string }>
  ): string;
}

/**
 * Creates a Swift bridge generator with injected dependencies
 */
export function createSwiftBridgeGenerator(
  deps: SwiftBridgeGeneratorDeps
): SwiftBridgeGenerator {
  const { typeMapper, codeEmitter } = deps;

  /**
   * Generates BrickCodegen.h umbrella header
   */
  function generateBrickCodegenHeader(): string {
    return `${codeEmitter.generateFileHeader()}
// This is the umbrella header for the BrickCodegen module
// Intentionally minimal; Swift is independent and ObjC bridge imports its own header.
`;
  }

  /**
   * Generates BrickModule.h header
   */
  function generateBrickModuleHeader(
    _events: Array<ModuleEvent & { moduleName: string }>
  ): string {
    return `${codeEmitter.generateFileHeader()}

#import <Foundation/Foundation.h>
#import "BrickModuleSpec.h"

#if __has_include("BrickCodegen/BrickCodegen-Swift.h")
#import "BrickCodegen/BrickCodegen-Swift.h"
#else
#import "BrickCodegen-Swift.h"
#endif


@interface BrickModule : NativeBrickModuleSpecBase<NativeBrickModuleSpec>
@end
`;
  }

  /**
   * Generates BrickModule.mm Objective-C bridge implementation
   */
  function generateObjectiveCBridge(
    allMethods: Array<ModuleMethod & { moduleName: string }>,
    allConstants: Array<ModuleConstant & { moduleName: string }>,
    allEvents: Array<ModuleEvent & { moduleName: string }>
  ): string {
    // Generate method implementations for New Architecture
    const newArchMethods = allMethods
      .map((method) => generateNewArchMethod(method))
      .join("\n\n");

    // Generate constants getters for both architectures
    const newArchConstantsGetter = generateNewArchConstantsGetter(allConstants);

    // (Deprecated) supported events array no longer used with CodegenTypes.EventEmitter

    const wrapperImpls = "";

    const bridgeContent = `${codeEmitter.generateFileHeader()}

#import "BrickModule.h"

@class BrickModuleImpl;

@interface BrickModule () <NativeBrickModuleSpec>
@property (nonatomic, strong) BrickModuleImpl *impl;
@end

@implementation BrickModule

RCT_EXPORT_MODULE()

@synthesize bridge = _bridge;

- (instancetype)init {
    self = [super init];
    if (self) {
        _impl = [BrickModuleImpl new];
        [self.impl setEventMap:[self getEventMap]];
    }
    return self;
}

- (NSDictionary *)getEventMap {
    typedef void (^BrickEventHandler)(NSDictionary *);
    NSMutableDictionary<NSString *, NSMutableDictionary<NSString *, BrickEventHandler> *> *eventMap = [NSMutableDictionary new];
    __weak BrickModule *weakSelf = self;
${generateEventMapEntries(allEvents)}
    return eventMap;
}

// No host attachment; app injects registry via KVC

// ========== METHODS ==========

${newArchMethods}

// ========== CONSTANTS ==========
${newArchConstantsGetter}

// ========== EVENT EMITTER WRAPPERS ==========
${wrapperImpls}

// No RCTEventEmitter-based methods; events are handled via CodegenTypes.EventEmitter

// TurboModule requirement
- (std::shared_ptr<facebook::react::TurboModule>)getTurboModule:
    (const facebook::react::ObjCTurboModule::InitParams &)params {
    RCTBridge* bridge = params.instance.bridge;
    [self.impl setBridge:bridge];
    return std::make_shared<facebook::react::NativeBrickModuleSpecJSI>(params);
}

+ (BOOL)requiresMainQueueSetup {
    return YES;
}

${generateGetRegisteredModulesMethod()}

${generateSetValueForKeyMethod()}

@end
`;

    return bridgeContent;
  }

  function generateEventMapEntries(
    events: Array<ModuleEvent & { moduleName: string }>
  ): string {
    if (!events || events.length === 0) {
      return "";
    }

    // Group events by module
    const eventsByModule = events.reduce<
      Record<string, Array<ModuleEvent & { moduleName: string }>>
    >((acc, e) => {
      if (!acc[e.moduleName]) {
        acc[e.moduleName] = [];
      }
      acc[e.moduleName]?.push(e);
      return acc;
    }, {});

    // Generate nested structure
    const lines: string[] = [];
    for (const [moduleName, moduleEvents] of Object.entries(eventsByModule)) {
      const moduleVarName = `${moduleName
        .charAt(0)
        .toLowerCase()}${moduleName.slice(1)}Events`;
      lines.push(
        `          NSMutableDictionary *${moduleVarName} = [NSMutableDictionary new];`
      );

      for (const e of moduleEvents) {
        const cap = codeEmitter.capitalizeFirst(e.name);
        const method = `emit${e.moduleName}_on${cap}`;
        lines.push(
          `          ${moduleVarName}[@"on${cap}"] = ^(NSDictionary *payload) { [weakSelf ${method}:payload]; };`
        );
      }

      lines.push(`          eventMap[@"${moduleName}"] = ${moduleVarName};`);
    }

    return lines.join("\n");
  }

  /**
   * Generates New Architecture method implementation
   */
  function generateNewArchMethod(
    method: ModuleMethod & { moduleName: string }
  ): string {
    const methodName = `${method.moduleName}_${method.name}`;

    // Generate parameter list with correct types from React Native Codegen
    const params = method.params
      .map((param, index) => {
        // Prefer parser-provided isFunction over string heuristics
        const objcType = param.isFunction
          ? "RCTResponseSenderBlock"
          : getReactNativeCodegenType(param.type, methodName, param.name);
        if (index === 0) {
          return `:(${objcType})${param.name}`;
        }
        return `${param.name}:(${objcType})${param.name}`;
      })
      .join(" ");

    // Sync methods now return NSDictionary for wrapped result
    const returnType = method.isAsync
      ? "void"
      : "NSDictionary *";

    // For async methods without parameters, we need to add a separator
    const resolveReject = method.isAsync
      ? method.params.length > 0
        ? " resolve:(RCTPromiseResolveBlock)resolve reject:(RCTPromiseRejectBlock)reject"
        : ":(RCTPromiseResolveBlock)resolve reject:(RCTPromiseRejectBlock)reject"
      : "";

    // Generate conversion code for complex objects
    const conversions = generateParameterConversions(method);
    const callParams = generateCallParameters(method);

    // For parameterless async methods, use WithResolve suffix
    const withSuffix =
      method.params.length > 0 ? "With" : method.isAsync ? "WithResolve" : "";
    const asyncParams = method.isAsync
      ? method.params.length > 0
        ? " resolve:resolve reject:reject"
        : ":resolve reject:reject"
      : "";
    // For sync methods, we need to add error parameter to the call
    const errorCallSuffix = method.isAsync ? "" : method.params.length > 0 ? " error:&error" : "WithError:&error";

    const callStatement = method.isAsync
      ? `[self.impl ${methodName}${withSuffix}${callParams}${asyncParams}];`
      : method.returnType === "void"
      ? `NSError *error = nil;
        BOOL success = [self.impl ${methodName}${withSuffix}${callParams}${errorCallSuffix}];
        if (success) {
            return @{
                @"~sync": @YES,
                @"success": @YES,
                @"value": [NSNull null]
            };
        } else {
            return @{
                @"~sync": @YES,
                @"success": @NO,
                @"error": @{
                    @"code": @"EXECUTION_ERROR",
                    @"message": error.localizedDescription != nil ? error.localizedDescription : @"Unknown error"
                }
            };
        }`
      : `NSError *error = nil;
        id value = [self.impl ${methodName}${withSuffix}${callParams}${errorCallSuffix}];
        if (value != nil) {
            return @{
                @"~sync": @YES,
                @"success": @YES,
                @"value": value
            };
        } else {
            return @{
                @"~sync": @YES,
                @"success": @NO,
                @"error": @{
                    @"code": @"EXECUTION_ERROR",
                    @"message": error.localizedDescription != nil ? error.localizedDescription : @"Unknown error"
                }
            };
        }`;

    return `- (${returnType})${methodName}${params}${resolveReject} {
${conversions}    ${callStatement}
}`;
  }

  /**
   * Gets the correct React Native Codegen type for complex objects
   */
  function getReactNativeCodegenType(
    tsType: string,
    methodName: string,
    paramName: string
  ): string {
    // Check if this is a callback/function type
    if (tsType === "callback") {
      return "RCTResponseSenderBlock";
    }

    // Check if this is a complex object type that would be generated by React Native Codegen
    if (
      (tsType.startsWith("{") && tsType.endsWith("}")) ||
      isAnyObjectType(tsType)
    ) {
      // This is an inline complex object - use the React Native Codegen generated type
      // Format: JS::NativeBrickModule::Spec{ModuleName}_{methodName}{CapitalizedParamName}
      // Example: Calculator_postMessage + parameter → SpecCalculator_postMessageParameter
      const parts = methodName.split("_");
      const moduleName = parts[0];
      const methodPart = parts.slice(1).join("_");
      const capitalizedParamName = codeEmitter.capitalizeFirst(paramName);
      const structName = `JS::NativeBrickModule::Spec${moduleName}_${methodPart}${capitalizedParamName}`;
      return `${structName} &`;
    }

    // Check if this is an interface type (PascalCase)
    if (tsType.match(/^[A-Z][a-zA-Z0-9]*$/)) {
      // This is an interface type - use the React Native Codegen generated type
      // Format: JS::NativeBrickModule::{ModuleName}_{InterfaceName}
      // Examples: User → Calculator_User, TwoDepthObj → Calculator_TwoDepthObj
      const moduleName = methodName.split("_")[0];
      const structName = `JS::NativeBrickModule::${moduleName}_${tsType}`;
      return `${structName} &`;
    }

    // Fall back to regular type mapping
    return typeMapper.mapTypeScriptToObjC(tsType, false, false);
  }

  /**
   * Generates parameter conversion code for complex objects
   */
  function generateParameterConversions(
    _method: ModuleMethod & { moduleName: string }
  ): string {
    // No conversions needed - we pass ._v directly to Swift
    return "";
  }

  /**
   * Generates call parameters for Swift method calls
   */
  function generateCallParameters(
    method: ModuleMethod & { moduleName: string }
  ): string {
    return method.params
      .map((param, index) => {
        // Use ._v accessor for complex objects (both inline objects and interface types)
        const isInline = param.type.startsWith("{") && param.type.endsWith("}");
        const isInterfaceLike = param.type.match(/^[A-Z][a-zA-Z0-9]*$/);
        const needsUnwrap = isInline || isInterfaceLike;
        const paramValue = needsUnwrap ? `${param.name}._v` : param.name;

        if (index === 0) {
          return `${codeEmitter.capitalizeFirst(param.name)}:${paramValue}`;
        }
        return ` ${param.name}:${paramValue}`;
      })
      .join("");
  }

  /**
   * Wraps Swift return values for Objective-C compatibility (New Architecture)
   */
  function wrapObjCReturnValueForNewArch(
    tsType: string,
    swiftCall: string
  ): string {
    // Handle Promise types
    if (tsType.startsWith("Promise<") && tsType.endsWith(">")) {
      return swiftCall; // async methods don't need wrapping
    }

    switch (tsType) {
      case "number":
        // For New Architecture, Swift methods return Double for numbers
        // We need to wrap it with @() to convert to NSNumber
        return `@(${swiftCall})`;
      case "boolean":
        // For New Architecture, Swift methods return NSNumber for booleans
        // The Swift extension already returns NSNumber, so no wrapping needed
        return swiftCall;
      case "string":
      case "void":
        // String and void don't need wrapping
        return swiftCall;
      default:
        // Arrays and complex objects don't need wrapping
        if (tsType.includes("[]") || tsType.startsWith("{")) {
          return swiftCall;
        }
        return swiftCall;
    }
  }

  /**
   * Generates constants getter for New Architecture
   */
  function generateNewArchConstantsGetter(
    constants: Array<ModuleConstant & { moduleName: string }>
  ): string {
    if (constants.length === 0) {
      return "";
    }

    const constantsEntries = constants
      .map((constant) => {
        const constantKey = `${constant.moduleName}_${constant.name}`;
        return `        .${constantKey} = [self.impl ${constant.moduleName}_${constant.name}],`;
      })
      .join("\n");

    return `- (facebook::react::ModuleConstants<JS::NativeBrickModule::Constants::Builder>)constantsToExport {
    return [self getConstants];
}

- (facebook::react::ModuleConstants<JS::NativeBrickModule::Constants::Builder>)getConstants {
    return facebook::react::typedConstants<JS::NativeBrickModule::Constants::Builder>({
${constantsEntries}
    });
}`;
  }

  // Add small utility bridged method for registry query
  function generateGetRegisteredModulesMethod(): string {
    return `- (NSArray<NSString *> *)getRegisteredModules {\n    return [self.impl getRegisteredModules];\n}`;
  }

  function generateSetValueForKeyMethod(): string {
    return `- (void)setValue:(id)value forKey:(NSString *)key {\n    if ([key isEqualToString:@"_brickModuleRegistry"]) {\n        @try {\n            [self.impl setValue:value forKey:@"brickModuleRegistry"];\n        } @catch (NSException *exception) {\n        }\n        return;\n    }\n    [super setValue:value forKey:key];\n}`;
  }

  /**
   * Generates supported events array
   */
  function generateSupportedEventsArray(
    events: Array<ModuleEvent & { moduleName: string }>
  ): string {
    if (events.length === 0) {
      return "    return @[];";
    }

    const eventNames = events.map(
      (event) => `@"${event.moduleName}_${event.name}"`
    );
    return `    return @[${eventNames.join(", ")}];`;
  }

  /**
   * Generates Swift supported events array
   */
  function generateSwiftSupportedEventsArray(
    _events: Array<ModuleEvent & { moduleName: string }>
  ): string {
    return "[String]()";
  }

  /**
   * Generates main BrickModule.swift file content
   */
  function generateBrickModuleSwift(
    allEvents: Array<ModuleEvent & { moduleName: string }>
  ): string {
    const swiftContent = `
import Foundation
import BrickModule

// MARK: - BrickModuleImpl Extension for Swift functionality

// Extension for BrickModuleImpl (called by ObjC++ thin wrapper)
extension BrickModuleImpl {
}

// ========== CONVERSION HELPERS ==========
// Generic Codable conversion helpers

extension BrickModuleImpl {
    func convertToDict<T: Codable>(_ object: T) throws -> [String: Any] {
        let jsonData = try JSONEncoder().encode(object)
        guard let dict = try JSONSerialization.jsonObject(with: jsonData, options: []) as? [String: Any] else {
            throw NSError(domain: "BrickModule", code: 500, userInfo: [NSLocalizedDescriptionKey: "Failed to convert to dictionary"])
        }
        return dict
    }

    func convertFromDict<T: Codable>(_ dict: [String: Any], to type: T.Type) throws -> T {
        let jsonData = try JSONSerialization.data(withJSONObject: dict, options: [])
        return try JSONDecoder().decode(type, from: jsonData)
    }
}
`;

    return swiftContent;
  }

  return {
    generateBrickCodegenHeader,
    generateBrickModuleHeader,
    generateObjectiveCBridge,
    generateBrickModuleSwift,
    generateSupportedEventsArray,
    generateSwiftSupportedEventsArray,
  };
}
